import React, { useMemo, useEffect, useState, useRef } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';

const getQuests = (t) => [
    {
        id: 'connect_2',
        title: t('questTracker.cosmicExpansion.title'),
        description: t('questTracker.cosmicExpansion.description'),
        target: 2,
        getProgress: (userConnections, initialConnections) => {
            return Math.max(0, userConnections.length - initialConnections);
        }
    },
    {
        id: 'inner_circle_1',
        title: t('questTracker.strengthenBonds.title'),
        description: t('questTracker.strengthenBonds.description'),
        target: 1,
        getProgress: (userConnections, initialInnerCircleCount) => {
            const currentInnerCircleCount = userConnections.filter(c => c.tier === 'Inner Circle').length;
            return Math.max(0, currentInnerCircleCount - initialInnerCircleCount);
        }
    },
];

const QuestTracker = () => {
    const { t } = useTranslation();
    const { userConnections } = useAppContext();
    const [isComplete, setIsComplete] = useState(false);

    const QUESTS = useMemo(() => getQuests(t), [t]);

    // Determine the quest of the day. Rotates daily.
    const quest = useMemo(() => {
        const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
        return QUESTS[dayOfYear % QUESTS.length];
    }, [QUESTS]);

    const initialValue = useRef(null);

    useEffect(() => {
        if (initialValue.current === null) {
            if (quest.id === 'connect_2') {
                initialValue.current = userConnections.length;
            } else if (quest.id === 'inner_circle_1') {
                initialValue.current = userConnections.filter(c => c.tier === 'Inner Circle').length;
            }
        }
    }, [userConnections, quest.id]);

    const progress = useMemo(() => {
        if (initialValue.current === null) return 0;
        return quest.getProgress(userConnections, initialValue.current);
    }, [userConnections, quest, initialValue]);

    const progressPercent = Math.min((progress / quest.target) * 100, 100);

    useEffect(() => {
        if (progressPercent >= 100) {
            setIsComplete(true);
        }
    }, [progressPercent]);

    return (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 mb-4 animate-fadeIn">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {isComplete ? 
                        <LucideIcons.CheckCircle2 size={24} className="text-green-400 flex-shrink-0" /> : 
                        <LucideIcons.Target size={24} className="text-cyan-400 flex-shrink-0" />
                    }
                    <div>
                        <p className={`font-bold ${isComplete ? 'text-green-400' : 'text-cyan-300'}`}>{isComplete ? t('questTracker.questComplete') : quest.title}</p>
                        <p className="text-xs text-gray-400">{quest.description}</p>
                    </div>
                </div>
                <div className="text-sm font-semibold text-white">
                    {progress} / {quest.target}
                </div>
            </div>
            <div className="w-full bg-black/50 rounded-full h-2.5 mt-2 border border-gray-700 overflow-hidden">
                <div 
                    className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-green-500' : 'bg-cyan-500'}`}
                    style={{ width: `${progressPercent}%` }}
                ></div>
            </div>
        </div>
    );
};

export default QuestTracker;
